import React from 'react';
import { FilterOptions } from '../types';
import { CARRIERS, STATIONS, EVENT_STATUS } from '../constants';
import { IropFilterOption } from '../utils/iropUtils';
import { todayDateZulu } from '../utils/dateUtils';
import StandaloneSkyWrenchLauncher from './standalone-skywrench-launcher';

/**
 * Props for the FilterBar component.
 */
interface FilterBarProps {
  filters: FilterOptions;
  searchQuery: string;
  onFilterChange: (field: keyof FilterOptions, value: string) => void;
  onSearchChange: (value: string) => void;
  /** Shows the non-production bulk-clear control immediately left of SkyWrench. */
  showClearAll?: boolean;
  clearAllDisabled?: boolean;
  clearAllBusy?: boolean;
  onClearAll?: () => void;
  showRecordType?: boolean;
  hideStatus?: boolean;
  showAtaCode?: boolean;
  carrierOptions?: string[];
  stationOptions?: string[];
  recordTypeOptions?: string[];
  tailNumberOptions?: string[];
  ataChapterOptions?: string[];
  /** IROP filter options (code + label). When provided, the IROP filter lists
   *  these instead of the Yes/No fallback. */
  iropOptions?: IropFilterOption[];
  /** When true, shows the RTS Date calendar field (left of Carrier). Used by the
   *  Completed Events view. */
  showRtsDate?: boolean;
  /** When true, shows the History-window (days) dropdown left of the search box. */
  showHistoryWindow?: boolean;
  /** Currently selected History-window size, in days. */
  historyWindowDays?: number;
  /** Selectable History-window sizes, in days. */
  historyWindowOptions?: number[];
  /** Invoked with the newly selected History-window size, in days. */
  onHistoryWindowChange?: (days: number) => void;
}

/**
 * Filter bar with dropdown selects and a search input.
 * Supports dynamic options linked to displayed data.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  searchQuery,
  onFilterChange,
  onSearchChange,
  showClearAll,
  clearAllDisabled,
  clearAllBusy,
  onClearAll,
  showRecordType,
  hideStatus,
  showAtaCode,
  carrierOptions,
  stationOptions,
  recordTypeOptions,
  tailNumberOptions,
  ataChapterOptions,
  iropOptions,
  showRtsDate,
  showHistoryWindow,
  historyWindowDays,
  historyWindowOptions,
  onHistoryWindowChange,
}) => {
  const carriers = carrierOptions || Object.values(CARRIERS);
  const stations = stationOptions || [...STATIONS];
  // Default the RTS Date calendar to today (Zulu) when no date is selected, so
  // the Completed Events view opens on the current date.
  const rtsDateValue = filters.rtsDate || todayDateZulu();

  return (
    <div className="filter-bar">
      {showRecordType && (
        <label className="filter-field">
          <span className="filter-label">Record Type</span>
          <select
            className="filter-select"
            value={filters.eventType}
            onChange={(e) => onFilterChange('eventType', e.target.value)}
          >
            <option value="">All</option>
            {(recordTypeOptions || ['Unscheduled', 'Planned', 'Deferred', 'Scheduled']).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      )}

      {showRtsDate && (
        <label className="filter-field">
          <span className="filter-label">RTS Date</span>
          <input
            className="filter-select"
            type="date"
            value={rtsDateValue}
            onChange={(e) => onFilterChange('rtsDate', e.target.value)}
            aria-label="RTS date"
          />
        </label>
      )}

      <label className="filter-field">
        <span className="filter-label">Carrier</span>
        <select
          className="filter-select"
          value={filters.carrier}
          onChange={(e) => onFilterChange('carrier', e.target.value)}
        >
          <option value="">All</option>
          {carriers.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      <label className="filter-field">
        <span className="filter-label">Tail Number</span>
        <select
          className="filter-select"
          value={filters.aircraft}
          onChange={(e) => onFilterChange('aircraft', e.target.value)}
        >
          <option value="">All</option>
          {(tailNumberOptions || []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      {showAtaCode && (
        <label className="filter-field">
          <span className="filter-label">ATA Code</span>
          <select
            className="filter-select"
            value={filters.ataChapter}
            onChange={(e) => onFilterChange('ataChapter', e.target.value)}
          >
            <option value="">All</option>
            {(ataChapterOptions || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="filter-field">
        <span className="filter-label">Station</span>
        <select
          className="filter-select"
          value={filters.station}
          onChange={(e) => onFilterChange('station', e.target.value)}
        >
          <option value="">All</option>
          {stations.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      <label className="filter-field">
        <span className="filter-label">IROP</span>
        <select
          className="filter-select"
          value={filters.irop}
          onChange={(e) => onFilterChange('irop', e.target.value)}
        >
          <option value="">All</option>
          {iropOptions ? (
            iropOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))
          ) : (
            <>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </>
          )}
        </select>
      </label>

      {!hideStatus && (
        <label className="filter-field">
          <span className="filter-label">Status</span>
          <select
            className="filter-select"
            value={filters.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
          >
            <option value="">All</option>
            <option value={EVENT_STATUS.PENDING}>Pending</option>
            <option value={EVENT_STATUS.IN_WORK}>In Work</option>
            <option value={EVENT_STATUS.COMPLETED}>Completed</option>
            <option value={EVENT_STATUS.DELETED}>Deleted</option>
          </select>
        </label>
      )}

      <label className="filter-field">
        <span className="filter-label">Spare</span>
        <select
          className="filter-select"
          value={filters.spare}
          onChange={(e) => onFilterChange('spare', e.target.value)}
        >
          <option value="">All</option>
        </select>
      </label>

      <div className="filter-spacer" />

      {showHistoryWindow && (
        <label className="filter-field">
          <span className="filter-label">History Window</span>
          <select
            className="filter-select"
            value={historyWindowDays ?? ''}
            onChange={(e) => onHistoryWindowChange?.(Number(e.target.value))}
            aria-label="History window in days"
          >
            {(historyWindowOptions || []).map((days) => (
              <option key={days} value={days}>
                {days} Days
              </option>
            ))}
          </select>
        </label>
      )}

      {showClearAll && (
        <button
          className="modal-btn modal-btn-danger clear-all-btn"
          type="button"
          disabled={clearAllDisabled || clearAllBusy}
          onClick={onClearAll}
        >
          {clearAllBusy ? 'Clearing…' : 'Clear All'}
        </button>
      )}

      <StandaloneSkyWrenchLauncher />

      <input
        className="filter-search"
        type="text"
        placeholder="Enter Search Text Here"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        aria-label="Search events"
      />

      <button className="filter-info-btn" type="button" aria-label="Information">
        &#9432;
      </button>
    </div>
  );
};

export default FilterBar;
